/**
 * I18nProvider — initializes i18next on mount and wraps children
 * in `I18nextProvider`.
 *
 * Renders nothing until the initial language bundle has finished loading
 * so child components never observe an undefined `t` function. Switching
 * `initialLanguage` after mount triggers a re-init through `initI18n`,
 * which is idempotent (it only swaps the active language).
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { i18n, initI18n } from '../domain/i18n';
import type { LanguageCode } from '../domain/types/preferences';

interface I18nProviderProps {
  initialLanguage: LanguageCode;
  children: ReactNode;
}

export function I18nProvider({ initialLanguage, children }: I18nProviderProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    initI18n(initialLanguage).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [initialLanguage]);

  if (!ready) return null;

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
